import posthog from 'posthog-js';
import { CurrentUserDTO } from 'services/api/models/UserDTO';

/**
 * PostHog evaluates feature flags for whichever `distinct_id` is currently
 * registered on the client. `posthog.identify()` swaps that id synchronously but
 * only *schedules* the matching `/flags` request (a 5ms debounce plus a round
 * trip), and posthog-js deliberately keeps serving the previously cached values
 * until the response lands.
 *
 * Every flag read in that window therefore returns the value evaluated for the
 * anonymous device id, with none of the person properties we attach at identify
 * time. Right after registration that window covered the whole post-signup
 * navigation, so a screen branching on a flag could render one variant while
 * PostHog assigned another.
 *
 * `identifyUser` records when that reload is outstanding and
 * `waitForFlagsForCurrentUser` resolves once it has landed, so `AuthContext` can
 * hold the auth status at 'loading' until the flags belong to the user we just
 * identified. Everything downstream is already gated on that status, so there is
 * nothing else to gate.
 */

// Flags are only fetched when PostHog is actually initialized (see index.tsx).
// Without a key there is no `/flags` request to wait for.
const POSTHOG_ENABLED = !!import.meta.env.VITE_POSTHOG_API_KEY;

// Fail open rather than blocking login indefinitely when `/flags` is slow, blocked
// by an ad blocker or quota-limited. Callers then read whatever is cached, which is
// the behaviour they had before this module existed.
const FLAGS_RELOAD_TIMEOUT_MS = 4000;

let flagsForCurrentUser: Promise<void> = Promise.resolve();

function personPropertiesFor(user: CurrentUserDTO): Record<string, any> {
  const properties: Record<string, any> = {
    gynger_is_caregiver: user.is_caregiver,
    gynger_access_tier: user.access_tier,
  };
  if (user.license_code) {
    properties.gynger_license_code = user.license_code;
  }
  return properties;
}

/**
 * Identifies the user in PostHog. The feature flags cached at this point still
 * belong to the previous (anonymous) identity, so callers should await
 * {@link waitForFlagsForCurrentUser} before letting anything read a flag.
 */
export function identifyUser(user: CurrentUserDTO): void {
  const distinctId = user.id;
  const previousDistinctId = posthog.get_distinct_id();

  if (user.license_code) {
    posthog.group('license_code', user.license_code);
  }
  posthog.identify(distinctId, personPropertiesFor(user));

  // posthog-js only reloads the flags when the distinct id actually changed, so
  // there is nothing to wait for when an already-identified user is restored from
  // stored tokens on a cold start.
  if (!POSTHOG_ENABLED || previousDistinctId === distinctId) return;

  flagsForCurrentUser = new Promise<void>(resolve => {
    // Assigned once both handles below exist; `settle` can only run later, from
    // the flags callback or the timer.
    let cleanup = () => {};
    const settle = () => {
      cleanup();
      resolve();
    };

    // `onFeatureFlags` invokes its handler synchronously when flags are already
    // cached, but that call still carries the pre-identify values — skip it and
    // wait for the reload that `identify()` just scheduled.
    let isSynchronousCall = true;
    const unsubscribe = posthog.onFeatureFlags(() => {
      if (isSynchronousCall) return;
      settle();
    });
    isSynchronousCall = false;

    const timer = setTimeout(settle, FLAGS_RELOAD_TIMEOUT_MS);

    cleanup = () => {
      unsubscribe();
      clearTimeout(timer);
    };
  });
}

/**
 * Resolves once the feature flags in cache were evaluated for the user most
 * recently passed to {@link identifyUser}. Never rejects, and always settles
 * within {@link FLAGS_RELOAD_TIMEOUT_MS} of the identify.
 */
export function waitForFlagsForCurrentUser(): Promise<void> {
  return flagsForCurrentUser;
}

/**
 * Refreshes the person properties and groups for the already-identified user.
 * The license code is both, so a change to it has to be pushed even though the
 * distinct id — and therefore the identity — has not moved.
 */
export function updateUserProperties(user: CurrentUserDTO): void {
  if (user.license_code) {
    posthog.group('license_code', user.license_code);
  }
  posthog.setPersonProperties(personPropertiesFor(user));
}

/** Resets the PostHog identity on logout. */
export function resetIdentity(): void {
  flagsForCurrentUser = Promise.resolve();
  posthog.reset();
}
